// src/shared/components/OeffentlicheSeite.tsx
//
// Rahmen der öffentlichen Seiten ohne Anmeldung (Einzeldokument,
// Dokumentenbereich, SEPA-Lastschrift, Passwort festlegen): Kopfleiste und
// Fußleiste über die volle Breite, wie im Kundenportal. Der Rahmen wird in
// App.tsx um die jeweilige Seite gelegt; die Seiten selbst enthalten weder
// Logo noch Fuß.
//
// Designkonvention (Freigabe Tim 02.11.2026): Logo links in der Kopfleiste,
// daneben durch eine feine Linie getrennt der Name der Anwendung in Grau. Die
// Überschrift auf der Seite nennt die Aufgabe der Seite.

import React from 'react';

interface OeffentlicheSeiteProps {
  /** Name der Anwendung neben dem Logo, z. B. "Dokumentenbereich" */
  anwendung: string;
  /** Hinweis auf die automatische Löschung nach 3 Jahren; nur, wo er zutrifft */
  loeschhinweis?: boolean;
  children: React.ReactNode;
}

const STIL = `
  .xo-seite { min-height: 100vh; display: flex; flex-direction: column; background-color: #f9fafb; }
  .xo-inhalt { flex: 1; }
  .xo-kopf { background: #ffffff; border-bottom: 1px solid #e5e7eb; }
  .xo-kopf-innen { max-width: 1100px; margin: 0 auto; padding: 14px 24px; display: flex; align-items: center; }
  .xo-kopf img { height: 40px; width: auto; display: block; }
  .xo-kopf span { margin-left: 14px; padding-left: 14px; border-left: 1px solid #e5e7eb; color: #6b7280; font-size: 15px; }
  .xo-fuss { background: #ffffff; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px; }
  .xo-fuss-innen { max-width: 1100px; margin: 0 auto; padding: 20px 24px; display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
  .xo-fuss p { margin: 2px 0; line-height: 1.5; }
  .xo-fuss-hinweis { max-width: 560px; }
  .xo-fuss-hinweis a { color: #3465de; text-decoration: none; }
  .xo-fuss-anschrift { text-align: right; flex-shrink: 0; }
  .xo-fuss-firma { font-weight: 600; color: #111827; }
  @media (max-width: 720px) {
    .xo-fuss-innen { flex-direction: column; }
    .xo-fuss-anschrift { text-align: left; }
  }
  /* Schmale Bildschirme: Innenabstände zählen zur Breite, sonst ragen die
     Karten der Seiten über den rechten Rand hinaus */
  @media (max-width: 600px) {
    .xo-inhalt * { box-sizing: border-box; }
  }
`;

export const OeffentlicheSeite: React.FC<OeffentlicheSeiteProps> = ({ anwendung, loeschhinweis = false, children }) => (
  <div className="xo-seite">
    <style>{STIL}</style>
    <header className="xo-kopf">
      <div className="xo-kopf-innen">
        <img src="/images/xtax-logo.png" alt="XTAX GmbH & Co. KG" />
        <span>{anwendung}</span>
      </div>
    </header>
    <div className="xo-inhalt">{children}</div>
    <footer className="xo-fuss">
      <div className="xo-fuss-innen">
        <div className="xo-fuss-hinweis">
          <p>
            🔒 Ihre Daten werden verschlüsselt übertragen und auf unseren Servern in Frankfurt am Main
            DSGVO-konform gehosted.{loeschhinweis && ' Nach 3 Jahren werden die Dokumente automatisch gelöscht.'}
          </p>
          <p>
            <a href="https://xtax.de/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung →</a>
          </p>
        </div>
        <div className="xo-fuss-anschrift">
          <p className="xo-fuss-firma">XTAX GmbH &amp; Co. KG</p>
          <p>Exzellente Lohnbuchhaltung vom Steuerberater</p>
          <p>Hansaring 78, 50670 Köln</p>
        </div>
      </div>
    </footer>
  </div>
);
